/**
 * Conversion and revenue event tracking
 */

import { analytics } from '../index';

export const conversionEvents = {
  // Subscription events
  trialStarted: async (data: {
    planName: string;
    trialDuration: number; // days
    referralSource?: string;
  }) => {
    await analytics.track('trial_started', {
      category: 'conversion',
      plan_name: data.planName,
      trial_duration: data.trialDuration,
      referral_source: data.referralSource,
    });
  },

  subscriptionStarted: async (data: {
    planName: string;
    billingInterval: 'monthly' | 'yearly';
    priceAmount: number;
    currency: string;
    isUpgrade: boolean;
    previousPlan?: string;
    paymentMethod: string;
  }) => {
    await analytics.track('subscription_started', {
      category: 'conversion',
      plan_name: data.planName,
      billing_interval: data.billingInterval,
      price_amount: data.priceAmount,
      currency: data.currency,
      is_upgrade: data.isUpgrade,
      previous_plan: data.previousPlan,
      payment_method: data.paymentMethod,
    });

    // Also track revenue
    await analytics.revenue(data.priceAmount, data.currency, {
      plan_name: data.planName,
      billing_interval: data.billingInterval,
    });
  },

  subscriptionUpgraded: async (data: {
    fromPlan: string;
    toPlan: string;
    priceDifference: number;
    currency: string;
    upgradeReason?: string;
  }) => {
    await analytics.track('subscription_upgraded', {
      category: 'conversion',
      from_plan: data.fromPlan,
      to_plan: data.toPlan,
      price_difference: data.priceDifference,
      currency: data.currency,
      upgrade_reason: data.upgradeReason,
    });

    // Track additional revenue
    if (data.priceDifference > 0) {
      await analytics.revenue(data.priceDifference, data.currency, {
        type: 'upgrade',
        from_plan: data.fromPlan,
        to_plan: data.toPlan,
      });
    }
  },

  subscriptionDowngraded: async (data: {
    fromPlan: string;
    toPlan: string;
    priceDifference: number;
    currency: string;
    downgradeReason?: string;
  }) => {
    await analytics.track('subscription_downgraded', {
      category: 'conversion',
      from_plan: data.fromPlan,
      to_plan: data.toPlan,
      price_difference: data.priceDifference,
      currency: data.currency,
      downgrade_reason: data.downgradeReason,
    });
  },

  subscriptionCancelled: async (data: {
    planName: string;
    cancellationReason?: string;
    feedbackProvided: boolean;
    subscriptionDuration: number; // days
    willRetry: boolean;
  }) => {
    await analytics.track('subscription_cancelled', {
      category: 'conversion',
      plan_name: data.planName,
      cancellation_reason: data.cancellationReason,
      feedback_provided: data.feedbackProvided,
      subscription_duration: data.subscriptionDuration,
      will_retry: data.willRetry,
    });
  },

  subscriptionRenewed: async (data: {
    planName: string;
    renewalCount: number;
    priceAmount: number;
    currency: string;
    isAutoRenew: boolean;
  }) => {
    await analytics.track('subscription_renewed', {
      category: 'conversion',
      plan_name: data.planName,
      renewal_count: data.renewalCount,
      price_amount: data.priceAmount,
      currency: data.currency,
      is_auto_renew: data.isAutoRenew,
    });

    // Track recurring revenue
    await analytics.revenue(data.priceAmount, data.currency, {
      type: 'renewal',
      plan_name: data.planName,
      renewal_count: data.renewalCount,
    });
  },

  // Checkout events
  checkoutStarted: async (data: {
    planName: string;
    priceAmount: number;
    currency: string;
    hasPromoCode: boolean;
  }) => {
    await analytics.track('checkout_started', {
      category: 'conversion',
      plan_name: data.planName,
      price_amount: data.priceAmount,
      currency: data.currency,
      has_promo_code: data.hasPromoCode,
    });
  },

  checkoutCompleted: async (data: {
    planName: string;
    priceAmount: number;
    currency: string;
    paymentMethod: string;
    promoCode?: string;
    discountAmount?: number;
  }) => {
    await analytics.track('checkout_completed', {
      category: 'conversion',
      plan_name: data.planName,
      price_amount: data.priceAmount,
      currency: data.currency,
      payment_method: data.paymentMethod,
      promo_code: data.promoCode,
      discount_amount: data.discountAmount,
    });
  },

  checkoutAbandoned: async (data: {
    planName: string;
    priceAmount: number;
    abandonmentStage: 'payment_info' | 'review' | 'processing';
    errorMessage?: string;
  }) => {
    await analytics.track('checkout_abandoned', {
      category: 'conversion',
      plan_name: data.planName,
      price_amount: data.priceAmount,
      abandonment_stage: data.abandonmentStage,
      error_message: data.errorMessage,
    });
  },

  // Payment events
  paymentFailed: async (data: {
    planName: string;
    failureReason: string;
    errorCode?: string;
    retryCount: number;
    willRetry: boolean;
  }) => {
    await analytics.track('payment_failed', {
      category: 'conversion',
      plan_name: data.planName,
      failure_reason: data.failureReason,
      error_code: data.errorCode,
      retry_count: data.retryCount,
      will_retry: data.willRetry,
    });
  },

  paymentMethodUpdated: async (data: {
    oldMethod: string;
    newMethod: string;
    updateReason?: string;
  }) => {
    await analytics.track('payment_method_updated', {
      category: 'conversion',
      old_method: data.oldMethod,
      new_method: data.newMethod,
      update_reason: data.updateReason,
    });
  },

  // Promo code events
  promoCodeApplied: async (data: {
    promoCode: string;
    discountType: 'percentage' | 'fixed';
    discountAmount: number;
    applicablePlans: string[];
  }) => {
    await analytics.track('promo_code_applied', {
      category: 'conversion',
      promo_code: data.promoCode,
      discount_type: data.discountType,
      discount_amount: data.discountAmount,
      applicable_plans: data.applicablePlans.join(','),
    });
  },

  promoCodeFailed: async (data: {
    promoCode: string;
    failureReason: 'invalid' | 'expired' | 'already_used' | 'not_applicable';
  }) => {
    await analytics.track('promo_code_failed', {
      category: 'conversion',
      promo_code: data.promoCode,
      failure_reason: data.failureReason,
    });
  },

  // Lead generation events
  leadCaptured: async (data: {
    leadSource: string;
    leadType: 'email' | 'demo_request' | 'contact_form' | 'newsletter';
    hasEmail: boolean;
    hasPhone: boolean;
  }) => {
    await analytics.track('lead_captured', {
      category: 'conversion',
      lead_source: data.leadSource,
      lead_type: data.leadType,
      has_email: data.hasEmail,
      has_phone: data.hasPhone,
    });
  },

  demoRequested: async (data: {
    requestSource: string;
    preferredTime?: string;
    specificFeatures?: string[];
  }) => {
    await analytics.track('demo_requested', {
      category: 'conversion',
      request_source: data.requestSource,
      preferred_time: data.preferredTime,
      specific_features: data.specificFeatures?.join(','),
    });
  },

  // Feature upgrade prompts
  upgradePromptShown: async (data: {
    promptType: string;
    triggerFeature: string;
    currentPlan: string;
    suggestedPlan: string;
  }) => {
    await analytics.track('upgrade_prompt_shown', {
      category: 'conversion',
      prompt_type: data.promptType,
      trigger_feature: data.triggerFeature,
      current_plan: data.currentPlan,
      suggested_plan: data.suggestedPlan,
    });
  },

  upgradePromptClicked: async (data: {
    promptType: string;
    action: 'learn_more' | 'upgrade_now' | 'dismiss';
    timeToAction: number; // seconds
  }) => {
    await analytics.track('upgrade_prompt_clicked', {
      category: 'conversion',
      prompt_type: data.promptType,
      action: data.action,
      time_to_action: data.timeToAction,
    });
  },

  // Referral events
  referralSent: async (data: {
    referralMethod: 'email' | 'link' | 'social';
    incentiveType?: string;
    recipientCount: number;
  }) => {
    await analytics.track('referral_sent', {
      category: 'conversion',
      referral_method: data.referralMethod,
      incentive_type: data.incentiveType,
      recipient_count: data.recipientCount,
    });
  },

  referralConverted: async (data: {
    referralSource: string;
    conversionValue: number;
    currency: string;
    rewardGiven: boolean;
  }) => {
    await analytics.track('referral_converted', {
      category: 'conversion',
      referral_source: data.referralSource,
      conversion_value: data.conversionValue,
      currency: data.currency,
      reward_given: data.rewardGiven,
    });
  },

  // Retention events
  winbackEmailClicked: async (data: {
    campaignName: string;
    daysSinceChurn: number;
    offerType?: string;
  }) => {
    await analytics.track('winback_email_clicked', {
      category: 'conversion',
      campaign_name: data.campaignName,
      days_since_churn: data.daysSinceChurn,
      offer_type: data.offerType,
    });
  },

  reactivationCompleted: async (data: {
    previousPlan: string;
    newPlan: string;
    inactiveDuration: number; // days
    reactivationOffer?: string;
  }) => {
    await analytics.track('reactivation_completed', {
      category: 'conversion',
      previous_plan: data.previousPlan,
      new_plan: data.newPlan,
      inactive_duration: data.inactiveDuration,
      reactivation_offer: data.reactivationOffer,
    });
  },
};